'use client';

import { api, useApiQuery } from '@/lib/api';
import { useMemo } from 'react';
import { IntegrationIdMap, IntegrationServiceDataMap } from '../utils';

export type IntegrationKey = keyof typeof IntegrationServiceDataMap;

/**
 * Which of the given integrations have at least one active agent in the
 * organization. Used to hide UI that is meaningless without the integration,
 * such as workflow presets that deep-link to integration-specific pages.
 */
export function useActiveIntegrations(integrations: IntegrationKey[]) {
  const applicationIds = integrations.map(
    (integration) => IntegrationServiceDataMap[integration].applicationId
  );

  const { data, isLoading } = useApiQuery((query) =>
    query.from(() =>
      api.accounts_integrationuseragent.query.all.queryOptions({
        filters: {
          applicationId: { in: applicationIds },
          isActive: { exact: true },
        },
        enabled: applicationIds.length > 0,
      })
    )
  );

  const active = useMemo(() => {
    const keys = new Set<IntegrationKey>();
    for (const agent of data ?? []) {
      const key =
        agent.applicationId && agent.serviceId
          ? IntegrationIdMap[agent.applicationId]?.[agent.serviceId]
          : undefined;
      if (key && key in IntegrationServiceDataMap) {
        keys.add(key as IntegrationKey);
      }
    }
    return keys;
  }, [data]);

  return { active, isLoading };
}
